import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AlertMessagesContext } from 'react-alert-messages';
import { Typography, Grid, Button, Box, Skeleton, IconButton } from '@mui/material';
import OnlineOrderService from 'services/OnlineOrderService';
import styles from './styles.module.css';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import CloseIcon from '@mui/icons-material/Close';
import ShopsHelper from 'helpers/ShopsHelper';
import { ShopContext } from 'Context/ShopContext';
import { useTranslation } from 'react-i18next';
import Info from 'components/utils/Alert/Info';
import DataTable from 'components/utils/DataTable';

const ORDER_STATUS = {
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
};

export default function OnlineOrderDetails({ order: orderProp, onClose, onCloseWithRealod }) {
  const { t: tCommon } = useTranslation("common");
  const { t: tOperations } = useTranslation("operations");
  const id = orderProp?.id;
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { shop } = useContext(ShopContext);

  const [order, setOrder] = useState(orderProp || null);
  const [loading, setLoading] = useState(false);
  const [confirmOpenCancel, setConfirmOpenCancel] = useState(null);
  const [confirmOpenRefund, setConfirmOpenRefund] = useState(null);

  const fetchOrderDetails = useCallback(async () => {
    if (!orderProp?.id) return;
    setLoading(true);
    try {
      const data = await OnlineOrderService.getOnlineOrderDetails(orderProp.id);
      setOrder(data);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setLoading(false);
    }
  }, [orderProp, postAlertMessage]);

  useEffect(() => {
    if (!order && orderProp?.id) {
      setLoading(true);
      fetchOrderDetails();
    }
  }, [order, orderProp, fetchOrderDetails]);

  const handleCancelOrder = async () => {
    setConfirmOpenCancel(false);
    setLoading(true);
    try {
      await OnlineOrderService.updateOrder(id, { status: ORDER_STATUS.CANCELLED });
      postAlertMessage({
        text: tOperations("Order Cancelled Successfully"),
        type: 'success',
      });

      onCloseWithRealod();
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleRefundOrder = async () => {
    setConfirmOpenRefund(false);
    setLoading(true);
    try {
      await OnlineOrderService.processRefund(orderProp.online_payment.id);

      postAlertMessage({
        text: tOperations("Online order cancelled and payment refunded successfully"),
        type: 'success',
      });

      onCloseWithRealod();
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !order) {
    return (
      <div className={styles.popUp}>
        <div className={styles.contentWrapperr}>
          <Skeleton variant="rectangular" height={50} animation="wave" />
          <Skeleton variant="rectangular" height={200} animation="wave" style={{ marginTop: 20 }} />
          <Skeleton variant="rectangular" height={300} animation="wave" style={{ marginTop: 20 }} />
        </div>
      </div>
    );
  }
  if (!order) return <Info title={tOperations("Not Found")} content={tOperations(tOperations("Order not found"))} />;

  const items = order.details?.items || [];

  const itemColumns = [
    { label: tCommon("Product"), id: 'name', type: 'text' },
    { label: tCommon("Qty"), id: 'quantity', type: 'text' },
    { label: tCommon("Rate"), id: 'rate', type: 'text' },
    {
      label: tCommon("Total"),
      id: 'total',
      type: 'callback',
      viewRender: (r) => ShopsHelper.getAmountFormatted(shop, r.quantity * r.rate),
    },
  ];

  const isPending = order.status === 'PENDING';

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapperr}>
        <div className={styles.titleSec}>
          <h2 className={styles.title}>
            {tOperations("Order Details")} <span className={styles.menuTitle}>#{order.id}</span>
          </h2>
          <div>
            <IconButton onClick={onClose} size="small" className={styles.closeBtn}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <div className={styles.changeable} style={{ paddingBottom: '8px' }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon("Customer")}</h3>
            </Grid>
            <Grid item xs={6} sm={4}>
              <h3 className={styles.inputLabel}>{order.customer?.name}</h3>
            </Grid>
            <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon("Mobile")}</h3>
            </Grid>
            <Grid item xs={6} sm={4}>
              <h3 className={styles.inputLabel}>{order.customer?.mobile}</h3>
            </Grid>

            <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon("Date")}</h3>
            </Grid>
            <Grid item xs={6} sm={4}>
              <h3 className={styles.inputLabel}>{DateTimeHelpers.convertDateToIsoDMY(order.created_at)}</h3>
            </Grid>
            <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon("Time")}</h3>
            </Grid>
            <Grid item xs={6} sm={4}>
              <h3 className={styles.inputLabel}>{DateTimeHelpers.convertTo12HourTime(order.created_at)}</h3>
            </Grid>

            <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon("Status")}</h3>
            </Grid>
            <Grid item xs={6} sm={4}>
              <h3 className={styles.inputLabel}>{order.status}</h3>
            </Grid>
            <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon("Total")}</h3>
            </Grid>
            <Grid item xs={6} sm={4}>
              <h3 className={styles.inputLabel}>{order.details?.amount}</h3>
            </Grid>
          </Grid>
        </div>

        {(loading || items.length > 0) ? (
          <DataTable className={styles.changeable} columns={itemColumns} rows={items || []} isLoading={loading} deactiveRow={false} />
        ) : (
          <Info
            title={tOperations("No items")}
            content={tOperations("You have no items to list")}
          />
        )}
        {isPending && (
          <div className={styles.changeable}>
            <Typography className={styles.subTitle}>{tCommon("Actions")}</Typography>
            <Box mb={2} display="flex" gap={2}>
              {orderProp?.online_payment && (
                <Button variant="contained" onClick={() => setConfirmOpenRefund(order)}>
                  {tOperations("Cancel Order")}
                </Button>)
              }
              <Button
                variant="contained"
                onClick={() => {
                  setConfirmOpenCancel(order);
                }}
              >
                {tOperations("Cancel Order Without Refund")}
              </Button>
            </Box>

            <ConfirmPopup
              data={confirmOpenCancel}
              handleClose={() => setConfirmOpenCancel(null)}
              handleOkay={handleCancelOrder}
              alertTitle={tOperations("Confirm Cancellation")}
              alertContent={tOperations("Are you sure you want to cancel this order?")}
              successBtnName={tOperations("Cancel Order")}
            />

            <ConfirmPopup
              data={confirmOpenRefund}
              handleClose={() => setConfirmOpenRefund(null)}
              handleOkay={handleRefundOrder}
              alertTitle={tOperations("Confirm Refund & Cancellation")}
              alertContent={tOperations("Are you sure you want to refund & cancel this order?")}
              successBtnName={tOperations("Cancel & Refund")}
            />
          </div>
        )}
      </div>
    </div>
  );
}
